'use client';
import React, { useEffect, useRef } from 'react';

const testimonials = [
  {
    quote: "Talent completely changed how I find brand deals. I went from cold DMs to having brands come to me with real budgets.",
    name: 'Aria Chen',
    role: 'TikTok Live Seller · Beauty',
    img: 30,
    gmv: '$320K/mo GMV',
  },
  {
    quote: "We found our top-performing live seller within 48 hours. She drove $180K in GMV in her first month with us. Unreal.",
    name: 'James Park',
    role: 'Head of Commerce · GlowCo Beauty',
    img: 52,
    gmv: '$180K first month',
  },
  {
    quote: "The verified GMV data is a game-changer. No more guessing if a seller can actually convert — the numbers speak for themselves.",
    name: 'Priya Sharma',
    role: 'TikTok Live Seller · Wellness',
    img: 25,
    gmv: '$195K/mo GMV',
  },
  {
    quote: "We scaled from 2 to 14 live sellers in 3 months using Talent. The matching engine is incredibly accurate for our niche.",
    name: 'Rachel Torres',
    role: 'VP Marketing · UrbanThread Co.',
    img: 44,
    gmv: '14 sellers hired',
  },
  {
    quote: "The contract and escrow system gives me peace of mind. I know I'll get paid, and brands know I'll deliver. Win-win.",
    name: 'Marcus Thompson',
    role: 'TikTok Live Seller · Fashion',
    img: 20,
    gmv: '$480K/mo GMV',
  },
  {
    quote: "Talent's analytics dashboard lets us see exactly which sellers are driving ROI. We doubled our live commerce revenue in Q1.",
    name: 'David Kim',
    role: 'E-Commerce Director · TechNova',
    img: 45,
    gmv: '2x revenue in Q1',
  },
];

export default function TestimonialsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.review-card');
            cards.forEach((card, i) => {
              setTimeout(() => {
                (card as HTMLElement).style.opacity = '1';
                (card as HTMLElement).style.transform = 'translateY(0)';
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-24 bg-neutral-01" ref={sectionRef}>
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Title */}
        <div className="text-center mb-14">
          <div className="mb-5">
            <div
              className="bg-theme-01 text-white text-[14px] font-semibold px-4 py-1.5 rounded-2xl inline-block"
              style={{ transform: 'rotate(-6deg)' }}
            >
              Testimonials
            </div>
          </div>
          <h2 className="text-[40px] md:text-[48px] font-bold text-dark leading-tight mb-4">
            What our users are saying
          </h2>
          <p className="text-[18px] text-dark/60 max-w-[560px] mx-auto leading-relaxed">
            Sellers and brands across every niche trust Talent to power their live commerce growth.
          </p>
        </div>

        {/* Testimonial cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <div
              key={i}
              className="review-card bg-white rounded-[32px] p-8 md:p-9 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer"
              style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.5s ease, transform 0.5s ease' }}
            >
              {/* Stars */}
              <div className="flex gap-1 mb-5">
                {[1,2,3,4,5].map((s) => (
                  <svg key={s} width="16" height="16" viewBox="0 0 24 24" fill="#FFD86F">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                ))}
              </div>

              {/* Quote */}
              <div className="mb-6">
                <h5 className="text-[17px] font-bold text-dark leading-snug mb-3">
                  &ldquo;{t.quote.split('.')[0]}.&rdquo;
                </h5>
                <p className="text-[14px] text-dark/60 leading-relaxed">
                  {t.quote.split('.').slice(1).join('.').trim()}
                </p>
              </div>

              {/* Reviewer */}
              <div className="flex items-center justify-between pt-5 border-t border-neutral-02">
                <div className="flex items-center gap-3">
                  <img
                    src={`https://i.pravatar.cc/48?img=${t.img}`}
                    alt={t.name}
                    className="w-11 h-11 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-bold text-dark text-[14px]">{t.name}</p>
                    <p className="text-dark/50 text-[12px]">{t.role}</p>
                  </div>
                </div>
                <span className="text-[11px] font-bold text-dark bg-theme-02 px-3 py-1 rounded-full flex-shrink-0">
                  {t.gmv}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
